import { SendableWordCountChange } from "@vericus/cadmus-editor-prosemirror";

import { toast } from "react-hot-toast";

import { client } from "@/client/apollo";
import { __GLOBAL_SESSION_ID } from "@/client/globals";
import {
  EditorType,
  LatestBlockSnapshotFragment,
  LatestBlockSnapshotFragmentDoc,
  SaveBlockSnapshotDocument,
  SaveBlockSnapshotMutation,
  SaveDocument,
  SaveMetadataInput,
  SaveMutation,
  SubmissionType,
  SubmitAttemptDocument,
  SubmitAttemptMutation,
  SubmitAttemptMutationVariables,
  SubmitDocument,
  SubmitMutation,
  SubmitMutationVariables,
  useUnsubmitMutation,
  WorkSubmitAttemptFragment,
  WorkSubmitAttemptFragmentDoc,
} from "@/generated/graphql";
import { matchGraphQLErrorCode } from "@/graphql/errors";
import { getCurrentDate } from "@/utils/datetime";

import { withRequestTimeout } from "../with-request-timeout";

// Saves use a shorter timeout than submits since the drain queue blocks on each.
const SUBMIT_REQUEST_TIMEOUT_MS = 120_000;
const SAVE_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Cloud save mutation to save all editors for the current Work.
 *
 * The `dispatchedAt` variable is a client-side generated timestamp but using a
 * server time-sync interface.
 *
 * @param workId the current student Work ID to save to
 * @param content serialised content of the save document
 * @param metadata extra information sent to the server for logging
 * @return graphql mutation promise
 */
export function onCloudSave(
  workId: string,
  content: string,
  metadata: SaveMetadataInput
) {
  return withRequestTimeout("save", SAVE_REQUEST_TIMEOUT_MS, (signal) =>
    client.mutate<SaveMutation>({
      mutation: SaveDocument,
      variables: {
        editor: EditorType.Prosemirror,
        workId,
        content,
        dispatchedAt: getCurrentDate().toISOString(),
        metadata,
      },
      context: { fetchOptions: { signal } },
    })
  );
}

/**
 * Write a Answer Block snapshot.
 *
 * @param workId the current student Work ID
 * @param answerBlockid the Answer Block being written
 * @param content serialised snapshot content
 * @param version incremental version number
 * @return graphql mutation promise
 */
export function onSnapshot(
  workId: string,
  answerBlockId: string,
  content: string,
  version: number,
  previousBlockSnapshotId: string | null,
  wordCountChange?: SendableWordCountChange
) {
  return withRequestTimeout(
    "saveBlockSnapshot",
    SAVE_REQUEST_TIMEOUT_MS,
    (signal) =>
      client.mutate<SaveBlockSnapshotMutation>({
        mutation: SaveBlockSnapshotDocument,
        variables: {
          workId,
          answerBlockId,
          snapshotContent: content,
          version,
          previousBlockSnapshotId,
          wordCountChange: wordCountChange
            ? JSON.stringify(wordCountChange)
            : null,
        },
        context: { fetchOptions: { signal } },
        update: (cache, { data: result }) => {
          const snapshot = result?.saveBlockSnapshot.blockSnapshot;
          if (snapshot) {
            cache.writeFragment<LatestBlockSnapshotFragment>({
              fragment: LatestBlockSnapshotFragmentDoc,
              fragmentName: "LatestBlockSnapshot",
              data: {
                __typename: "AnswerBlock",
                id: answerBlockId,
                latestBlockSnapshot: {
                  __typename: "BlockSnapshot",
                  id: snapshot.id,
                  cacheId: `${answerBlockId}:latest_snapshot`,
                  answerBlockId,
                  version: version,
                  content: content,
                  blockName: snapshot.blockName,
                },
              },
            });
          }
        },
      })
  );
}

/**
 * Submit mutation for both Draft and Final submissions.
 */
export function onSubmit(
  workId: string,
  saveId: string,
  type: SubmissionType,
  submittedAt: string
) {
  return withRequestTimeout("submit", SUBMIT_REQUEST_TIMEOUT_MS, (signal) =>
    client.mutate<SubmitMutation, SubmitMutationVariables>({
      mutation: SubmitDocument,
      variables: {
        editor: EditorType.Prosemirror,
        workId,
        saveId,
        type,
        submittedAt,
        acceptedDeclaration: true,
        sessionId: __GLOBAL_SESSION_ID.current,
      },
      context: { fetchOptions: { signal } },
    })
  );
}

/**
 * Make a Submission attempt for both Draft and Final submissions.
 */
export function onSubmitAttempt({
  workId,
  attemptNumber,
  type,
  submissionDate,
}: SubmitAttemptMutationVariables) {
  return withRequestTimeout(
    "submitAttemptV2",
    SUBMIT_REQUEST_TIMEOUT_MS,
    (signal) =>
      client.mutate<SubmitAttemptMutation, SubmitAttemptMutationVariables>({
        mutation: SubmitAttemptDocument,
        variables: {
          workId,
          attemptNumber,
          type,
          submissionDate,
        },
        context: { fetchOptions: { signal } },
        // If there are existing `Result` objects in the Query, they will normally
        // get overwritten. But if there are no existing results, then the incoming
        // `Result` will be orphaned and not available unless directly queried.
        //
        // Therefore we must ensure the `Result` objects are explicitly added to the
        // `Query` cache at the `Work.result(submissionType: FINAL)` and
        // `Work.result(submissionType: DRAFT)` fields (aliased `finalResult` and
        // `draftResult` in the `WorkSubmitAttempt` fragment).
        update: (cache, { data }) => {
          if (data && data.submitAttemptV2) {
            cache.updateFragment<WorkSubmitAttemptFragment>(
              {
                id: `Work:${workId}`,
                fragment: WorkSubmitAttemptFragmentDoc,
                fragmentName: "WorkSubmitAttempt",
              },
              (fragmentData) => {
                const workData =
                  fragmentData ||
                  ({
                    __typename: "Work",
                    id: workId,
                    attemptCount: 0,
                    draftResult: null,
                    finalResult: null,
                  } satisfies WorkSubmitAttemptFragment);

                return {
                  ...workData,
                  attemptCount: data.submitAttemptV2.nextAttemptNumber,
                  finalResult:
                    type === SubmissionType.Final
                      ? data.submitAttemptV2.result
                      : workData.finalResult,
                  draftResult:
                    type === SubmissionType.Draft
                      ? data.submitAttemptV2.result
                      : workData.draftResult,
                };
              }
            );
          }
        },
      })
  );
}

/**
 * Mutation hook to unsubmit a FINAL submission for a given Work and increment
 * the Work attempt count for the next FINAL submission.
 *
 * When a FINAL submission is made in an Assignment or Exam, the client moves to
 * a locked state. To return to editing the Work, the student may unsubmit their
 * FINAL submission (setting `unsubmittedAt` timestamp), allowing them to edit
 * and make another attempt.
 *
 * Using this hook ensures the Apollo cache is updated accordingly to reflect
 * the new available attempt for the next submission.
 *
 * @param workId the Work ID to unsubmit
 * @param submissionId the FINAL Submission ID to unsubmit
 * @param onCompleted optional callback when mutation is completed
 * @return mutation tuple matching what Apollo's useMutation returns.
 */
export function useUnsubmit(
  workId: string,
  submissionId: string,
  onCompleted?: () => void
) {
  return useUnsubmitMutation({
    variables: { submissionId: submissionId, workId: workId },
    onCompleted,
    onError: (error) => {
      // This should only error if there is a disconnect between client and
      // server state regarding the current work's attempt counts.
      if (matchGraphQLErrorCode(error, ["VALIDATION_ERROR"])) {
        toast.error(
          "Maximum number of attempts reached. Please contact your instructor."
        );
      }
    },
  });
}
